import { FilterModel, SortModelItem } from 'ag-grid-community';
import { addDays, formatISO, parseISO } from 'date-fns';

const TEXT_OP_MAP: Record<string, string> = {
  contains: 'contains',
  notContains: 'ncontains',
  equals: 'eq',
  notEqual: 'neq',
  startsWith: 'starts',
  endsWith: 'ends',
  blank: 'eq',
  notBlank: 'neq',
};

const NUMBER_OP_MAP: Record<string, string> = {
  equals: 'eq',
  notEqual: 'neq',
  greaterThan: 'gt',
  greaterThanOrEqual: 'gte',
  lessThan: 'lt',
  lessThanOrEqual: 'lte',
  blank: 'eq',
  notBlank: 'neq',
};

const DATE_OP_MAP: Record<string, string> = {
  equals: 'eq',
  notEqual: 'neq',
  lessThan: 'lt',
  greaterThan: 'gt',
  lessThanOrEqual: 'lte',
  greaterThanOrEqual: 'gte',
  blank: 'eq',
  notBlank: 'neq',
};

function toDateOnly(dateStr: string): string {
  return formatISO(parseISO(dateStr), { representation: 'date' });
}

function nextDay(dateStr: string): string {
  const date = parseISO(dateStr);
  return formatISO(addDays(date, 1), { representation: 'date' });
}

function writeParam(
  searchParams: URLSearchParams,
  field: string,
  value: string,
  isFirst: boolean,
): void {
  if (isFirst) {
    searchParams.set(field, value);
  } else {
    searchParams.append(field, value);
  }
}

function appendCondition(
  searchParams: URLSearchParams,
  field: string,
  condition: Record<string, unknown>,
  isFirst: boolean,
): void {
  const filterType = condition.filterType as string;
  const type = condition.type as string | undefined;

  if (!filterType) {
    console.error('Unsupported filter condition', {
      field,
      condition,
    });
    return;
  }

  if (filterType === 'set') {
    const values = condition.values;
    if (!Array.isArray(values)) {
      console.error('Unsupported set filter condition', {
        field,
        condition,
      });
      return;
    }

    if (values.length > 0) {
      writeParam(searchParams, field, `in:${values.join(',')}`, isFirst);
    }
    return;
  }

  if (!type) {
    console.error('Unsupported filter condition', {
      field,
      condition,
    });
    return;
  }

  const isBlankType = type === 'blank' || type === 'notBlank';

  switch (filterType) {
    case 'text': {
      const op = TEXT_OP_MAP[type];
      if (!op) {
        console.error('Unsupported text filter operation', {
          field,
          condition,
        });
        return;
      }
      const value = isBlankType ? 'null' : (condition.filter as string);
      if (value == null) {
        console.error('Unsupported text filter condition', {
          field,
          condition,
        });
        return;
      }
      writeParam(searchParams, field, `${op}:${value}`, isFirst);
      break;
    }

    case 'number': {
      if (type === 'inRange') {
        if (condition.filter == null && condition.filterTo == null) {
          console.error('Unsupported number filter range', {
            field,
            condition,
          });
          return;
        }
        if (condition.filter != null) {
          writeParam(searchParams, field, `gte:${condition.filter}`, isFirst);
        }
        if (condition.filterTo != null) {
          searchParams.append(field, `lte:${condition.filterTo}`);
        }
        return;
      }
      const op = NUMBER_OP_MAP[type];
      if (!op) {
        console.error('Unsupported number filter operation', {
          field,
          condition,
        });
        return;
      }
      const value = isBlankType ? 'null' : condition.filter;
      if (value == null) {
        console.error('Unsupported number filter condition', {
          field,
          condition,
        });
        return;
      }
      writeParam(searchParams, field, `${op}:${value}`, isFirst);
      break;
    }

    case 'date': {
      if (type === 'inRange') {
        if (!condition.dateFrom && !condition.dateTo) {
          console.error('Unsupported date filter range', {
            field,
            condition,
          });
          return;
        }
        if (condition.dateFrom) {
          writeParam(
            searchParams,
            field,
            `gte:${toDateOnly(condition.dateFrom as string)}`,
            isFirst,
          );
        }
        if (condition.dateTo) {
          searchParams.append(
            field,
            `lte:${toDateOnly(condition.dateTo as string)}`,
          );
        }
        return;
      }
      if (type === 'equals' && !isBlankType) {
        if (condition.dateFrom) {
          const from = toDateOnly(condition.dateFrom as string);
          writeParam(searchParams, field, `gte:${from}`, isFirst);
          searchParams.append(
            field,
            `lt:${nextDay(condition.dateFrom as string)}`,
          );
        }
        return;
      }
      const op = DATE_OP_MAP[type];
      if (!op) {
        console.error('Unsupported date filter operation', {
          field,
          condition,
        });
        return;
      }
      const value = isBlankType
        ? 'null'
        : condition.dateFrom
          ? toDateOnly(condition.dateFrom as string)
          : null;
      if (value == null) {
        console.error('Unsupported date filter condition', {
          field,
          condition,
        });
        return;
      }
      writeParam(searchParams, field, `${op}:${value}`, isFirst);
      break;
    }

    default: {
      console.error('Unsupported filter type', {
        field,
        condition,
      });
      break;
    }
  }
}

type FilterLeaf = Record<string, Record<string, unknown>>;

type FilterNode = FilterLeaf | { $and: FilterNode[] } | { $or: FilterNode[] };

const TEXT_FILTER_OP_MAP: Record<string, string> = {
  contains: '$contains',
  notContains: '$ncontains',
  equals: '$eq',
  notEqual: '$neq',
  startsWith: '$starts',
  endsWith: '$ends',
  blank: '$eq',
  notBlank: '$neq',
};

const NUMBER_FILTER_OP_MAP: Record<string, string> = {
  equals: '$eq',
  notEqual: '$neq',
  greaterThan: '$gt',
  greaterThanOrEqual: '$gte',
  lessThan: '$lt',
  lessThanOrEqual: '$lte',
  blank: '$eq',
  notBlank: '$neq',
};

const DATE_FILTER_OP_MAP: Record<string, string> = {
  equals: '$eq',
  notEqual: '$neq',
  lessThan: '$lt',
  greaterThan: '$gt',
  lessThanOrEqual: '$lte',
  greaterThanOrEqual: '$gte',
  blank: '$eq',
  notBlank: '$neq',
};

function leaf(field: string, op: string, value: unknown): FilterLeaf {
  return { [field]: { [op]: value } };
}

function conditionToLeaves(
  field: string,
  condition: Record<string, unknown>,
): FilterLeaf[] {
  const filterType = condition.filterType as string | undefined;
  const type = condition.type as string | undefined;

  if (!filterType) {
    console.error('Unsupported filter condition', { field, condition });
    return [];
  }

  if (filterType === 'set') {
    const values = condition.values;
    if (!Array.isArray(values)) {
      console.error('Unsupported set filter condition', { field, condition });
      return [];
    }
    return values.length > 0 ? [leaf(field, '$in', values)] : [];
  }

  if (!type) {
    console.error('Unsupported filter condition', { field, condition });
    return [];
  }

  const isBlankType = type === 'blank' || type === 'notBlank';

  switch (filterType) {
    case 'text': {
      const op = TEXT_FILTER_OP_MAP[type];
      if (!op) {
        console.error('Unsupported text filter operation', {
          field,
          condition,
        });
        return [];
      }
      const value = isBlankType ? null : (condition.filter as string);
      if (value == null && !isBlankType) {
        console.error('Unsupported text filter condition', {
          field,
          condition,
        });
        return [];
      }
      return [leaf(field, op, value)];
    }

    case 'number': {
      if (type === 'inRange') {
        if (condition.filter == null && condition.filterTo == null) {
          console.error('Unsupported number filter range', {
            field,
            condition,
          });
          return [];
        }
        const leaves: FilterLeaf[] = [];
        if (condition.filter != null)
          leaves.push(leaf(field, '$gte', condition.filter));
        if (condition.filterTo != null)
          leaves.push(leaf(field, '$lte', condition.filterTo));
        return leaves;
      }
      const op = NUMBER_FILTER_OP_MAP[type];
      if (!op) {
        console.error('Unsupported number filter operation', {
          field,
          condition,
        });
        return [];
      }
      const value = isBlankType ? null : condition.filter;
      if (value == null && !isBlankType) {
        console.error('Unsupported number filter condition', {
          field,
          condition,
        });
        return [];
      }
      return [leaf(field, op, value)];
    }

    case 'date': {
      if (type === 'inRange') {
        if (!condition.dateFrom && !condition.dateTo) {
          console.error('Unsupported date filter range', { field, condition });
          return [];
        }
        const leaves: FilterLeaf[] = [];
        if (condition.dateFrom)
          leaves.push(
            leaf(field, '$gte', toDateOnly(condition.dateFrom as string)),
          );
        if (condition.dateTo)
          leaves.push(
            leaf(field, '$lte', toDateOnly(condition.dateTo as string)),
          );
        return leaves;
      }
      if (type === 'equals' && !isBlankType) {
        if (!condition.dateFrom) return [];
        return [
          leaf(field, '$gte', toDateOnly(condition.dateFrom as string)),
          leaf(field, '$lt', nextDay(condition.dateFrom as string)),
        ];
      }
      const op = DATE_FILTER_OP_MAP[type];
      if (!op) {
        console.error('Unsupported date filter operation', {
          field,
          condition,
        });
        return [];
      }
      const value = isBlankType
        ? null
        : condition.dateFrom
          ? toDateOnly(condition.dateFrom as string)
          : undefined;
      if (value === undefined) {
        console.error('Unsupported date filter condition', {
          field,
          condition,
        });
        return [];
      }
      return [leaf(field, op, value)];
    }

    default: {
      console.error('Unsupported filter type', { field, condition });
      return [];
    }
  }
}

export type BuildFilterParamOptions = {
  /**
   * Text Blank/Not Blank also match '' (not just null). Only safe when every
   * text-filtered field is a string column; the API rejects '' for int/bool.
   */
  textBlankMatchesEmpty?: boolean;
};

function conditionToNode(
  field: string,
  condition: Record<string, unknown>,
  options?: BuildFilterParamOptions,
): FilterNode | null {
  const type = condition.type as string | undefined;
  const blankMatchesEmpty =
    options?.textBlankMatchesEmpty && condition.filterType === 'text';
  if (blankMatchesEmpty && type === 'blank') {
    return {
      $or: [leaf(field, '$eq', null), leaf(field, '$eq', '')],
    };
  }
  if (blankMatchesEmpty && type === 'notBlank') {
    return {
      $and: [leaf(field, '$neq', null), leaf(field, '$neq', '')],
    };
  }

  if (condition.filterType === 'set' && Array.isArray(condition.values)) {
    const values = condition.values;
    const nonNullValues = values.filter((value) => value != null);
    const includesNull = nonNullValues.length !== values.length;

    if (includesNull) {
      const nullNode = leaf(field, '$eq', null);
      if (nonNullValues.length === 0) return nullNode;
      return {
        $or: [nullNode, leaf(field, '$in', nonNullValues)],
      };
    }
  }

  return andLeaves(conditionToLeaves(field, condition));
}

function andLeaves(leaves: FilterLeaf[]): FilterNode | null {
  if (leaves.length === 0) return null;
  if (leaves.length === 1) return leaves[0];
  return { $and: leaves };
}

function buildFieldNode(
  field: string,
  condition: Record<string, unknown>,
  options?: BuildFilterParamOptions,
): FilterNode | null {
  const operator = condition.operator as 'AND' | 'OR' | undefined;

  let subConditions: Record<string, unknown>[] | null = null;
  if (operator && Array.isArray(condition.conditions)) {
    subConditions = condition.conditions as Record<string, unknown>[];
  } else if (operator && condition.condition1) {
    subConditions = [condition.condition1 as Record<string, unknown>];
    if (condition.condition2)
      subConditions.push(condition.condition2 as Record<string, unknown>);
  }

  if (!subConditions) {
    return conditionToNode(field, condition, options);
  }

  const childNodes = subConditions
    .map((c) => conditionToNode(field, c, options))
    .filter((n): n is FilterNode => n !== null);

  if (childNodes.length === 0) return null;
  if (childNodes.length === 1) return childNodes[0];

  return operator === 'OR' ? { $or: childNodes } : { $and: childNodes };
}

export function buildFilterParam(
  filterModel: FilterModel | null | undefined,
  fieldMappings?: Record<string, string>,
  options?: BuildFilterParamOptions,
): string | null {
  if (!filterModel) return null;

  const fieldNodes: FilterNode[] = [];
  for (const [field, condition] of Object.entries(filterModel)) {
    const mappedField = fieldMappings?.[field] ?? field;
    const node = buildFieldNode(
      mappedField,
      condition as Record<string, unknown>,
      options,
    );
    if (node) fieldNodes.push(node);
  }

  if (fieldNodes.length === 0) return null;
  const root: FilterNode =
    fieldNodes.length === 1 ? fieldNodes[0] : { $and: fieldNodes };
  return JSON.stringify(root);
}

/**
 * Translates AG Grid filterModel, sortModel, and pagination into
 * query string parameters per the REST API query spec.
 *
 * Mutates and returns the provided URLSearchParams so callers can
 * pre-populate it with their own params (e.g. sidebar filters) before
 * calling this function.
 */
export function addGridQueryParams(
  searchParams: URLSearchParams,
  filterModel: FilterModel | null | undefined,
  sortModel: SortModelItem[],
  startRow: number,
  endRow: number,
  fieldMappings?: Record<string, string>,
): URLSearchParams {
  const clone = new URLSearchParams(searchParams);

  if (filterModel) {
    for (const [field, condition] of Object.entries(filterModel)) {
      const mappedField = fieldMappings?.[field] ?? field;
      if (condition.operator && condition.conditions) {
        const conditions = condition.conditions as Record<string, unknown>[];
        for (let i = 0; i < conditions.length; i++) {
          appendCondition(clone, mappedField, conditions[i], i === 0);
        }
      } else if (condition.operator && condition.condition1) {
        appendCondition(
          clone,
          mappedField,
          condition.condition1 as Record<string, unknown>,
          true,
        );
        if (condition.condition2) {
          appendCondition(
            clone,
            mappedField,
            condition.condition2 as Record<string, unknown>,
            false,
          );
        }
      } else {
        appendCondition(
          clone,
          mappedField,
          condition as Record<string, unknown>,
          true,
        );
      }
    }
  }

  for (let i = 0; i < sortModel.length; i++) {
    const { colId, sort } = sortModel[i];
    const mappedColId = fieldMappings?.[colId] ?? colId;
    if (i === 0) {
      clone.set('order', `${mappedColId}:${sort}`);
    } else {
      clone.append('order', `${mappedColId}:${sort}`);
    }
  }

  clone.set('skip', startRow.toString());
  clone.set('take', (endRow - startRow).toString());

  return clone;
}
